import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";

import { MatSelectionListChange } from "@angular/material/list";

import { TranslateService } from "@ngx-translate/core";

import { combineLatest, Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  createStoreProductList,
  getSiteStoreProductList,
} from "src/app/store/actions/sites/sites.actions";
import { getProductsList } from "src/app/store/actions/products/product.actions";
import {
  AppState,
  loadedProductIsLoadingState,
  loadedProductListState,
  loadedProductTotalState,
  siteStoreProductListLoadedState,
} from "src/app/store/reducers";

import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { ProductDTO } from "src/app/models/Products/productDTO";
import { FieldType, FilterName } from "src/app/models/custom-table/custom-table.model";
import { SelectionItem } from "src/app/models/selection-list/selection-list.model";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { MatPaginator } from "@angular/material/paginator";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "store-products",
  templateUrl: "./store-products.component.html",
  styleUrls: ["./store-products.component.scss"],
})
export class StoreProductsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() siteCode: string;

  multiSubs: Subscription;

  //Store Products
  isLoading$: Observable<boolean>;
  storeProductsList$: Observable<ProductDTO[]>;
  storeTotalProducts$: Observable<number>;
  storeProductsList: ProductDTO[];

  //Selected Store Products
  selectedStoreProducts$: Observable<SiteProductDTO[]>;
  selectedStoreProducts: SiteProductDTO[];

  searchTerms: string[] = [];

  isNotFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: false
  };

  isAllSelected = false;

  constructor(
    private translate: TranslateService,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.isLoading$ = this.store.pipe(select(loadedProductIsLoadingState));
    this.storeProductsList$ = this.store.pipe(select(loadedProductListState));
    this.storeTotalProducts$ = this.store.pipe(select(loadedProductTotalState));
    this.selectedStoreProducts$ = this.store.pipe(select(siteStoreProductListLoadedState));
  }

  ngOnInit() {
    this.setSubscriptions();
    this.setDispatchers();
  }
  
  setSubscriptions() {
    this.multiSubs = combineLatest([
      this.storeProductsList$.pipe(skip(1)),
      this.selectedStoreProducts$.pipe(skip(1))
    ]).subscribe(([storeProductsList, selectedStoreProducts]) => {
      if (storeProductsList) {
        this.storeProductsList = this.setStoreProductsList(storeProductsList);
      }

      if (selectedStoreProducts) {
        this.selectedStoreProducts = this.setSelectedStoreProducts(selectedStoreProducts);
      }

      if (storeProductsList && selectedStoreProducts) {
        this.storeProductsList = this.setStoreProductsListWithGrossUnitPrice(storeProductsList);
      }
    });
  }

  setStoreProductsList(storeProductsList: ProductDTO[]) {
    return storeProductsList.map((product) => ({
      ...product,
      style: product.color
        ? `background-color: #${product.color}; color: #FFF;`
        : null,
    }));
  }

  setSelectedStoreProducts(selectedStoreProducts: SiteProductDTO[]) {
    return selectedStoreProducts.map(
      (selectedProduct) => ({ 
        productCode: selectedProduct.productCode, 
        favorite: selectedProduct.favorite,
        enabled: selectedProduct.enabled,
        unitPriceGross:
          selectedProduct.unitPriceGross ||
          selectedProduct.unitPriceGross === 0
            ? selectedProduct.unitPriceGross
            : selectedProduct.unitPriceGrossReference ||
              selectedProduct.unitPriceGrossReference === 0
            ? selectedProduct.unitPriceGrossReference
            : 0,
      })
    );
  }

  setStoreProductsListWithGrossUnitPrice(storeProductsList: ProductDTO[]) {
    return storeProductsList.map((product) => {
      const { code } = product;
      const productFound = this.selectedStoreProducts.find(({ productCode }) => productCode === code);
      return { ...product, unitPriceGross: productFound?.unitPriceGross };
    }); 
  }
  
  setDispatchers() {
    this.store.dispatch(
      getProductsList({
        options: {
          paging: {
            skip: 0,
            top: 10,
            realSize: true,
          },
          advancedFilteringArgs: { advancedFilter: [ this.isNotFuelFilter ]},
        },
      })
    );
    this.store.dispatch(
      getSiteStoreProductList({ code: this.siteCode })
    );
  }

  getPagedData() {
    const { pageIndex, pageSize } = this.paginator;
    this.store.dispatch(
      getProductsList({
        options: {
          paging: {
            skip: pageIndex * pageSize,
            top: pageSize,
            realSize: true,
          },
          advancedFilteringArgs: { advancedFilter: [ this.isNotFuelFilter ]},
        },
      })
    );
    this.isAllSelected = false;
  }

  checkSelected(item: SelectionItem): boolean {
    const { code } = item;
    if (!this.selectedStoreProducts || !code) {
      return;
    }
    return this.selectedStoreProducts.some(
      ({ productCode }) => productCode === code
    );
  }

  onSelectionChange(event: any, multipleSelection = false) {
    const [option] = event.options;
    const { value: { code } } = option;

    const found = !multipleSelection && this.selectedStoreProducts
      ? this.selectedStoreProducts.some(
          ({ productCode }) => productCode === code
        )
      : false;
    if (found) {
      this.selectedStoreProducts = this.selectedStoreProducts.filter(
        ({ productCode }) => productCode !== code
      );
    } else {
      this.selectedStoreProducts = this.selectedStoreProducts
        ? [
            ...this.selectedStoreProducts,
            {
              productCode: code,
              favorite: null,
              enabled: true,
              unitPriceGross: undefined,
            },
          ]
        : [
            {
              productCode: code,
              favorite: null,
              enabled: true,
              unitPriceGross: undefined,
            },
          ];
    }

    if (!multipleSelection) {
      this.updateSiteStoreProducts(this.selectedStoreProducts);
    }
  }

  setStoreProductSearchTooltip() {
    const searchTooltipHint = this.translate.instant("SimpleFilter.HintTitle");
    const searchableFields = [
      this.translate.instant("SiteManagement.Code"),
      this.translate.instant("SiteManagement.Description"),
    ];
    return `${searchTooltipHint}\n${searchableFields.join("\n")}`;
  }

  searchStoreProducts(searchTerms: string[]) {
    const { pageIndex, pageSize } = this.paginator;
    this.searchTerms = searchTerms;
    this.store.dispatch(
      getProductsList({
        options: {
          paging: {
            skip: pageIndex * pageSize,
            top: pageSize,
            realSize: true,
          },
          filteringArgs: {
            filterConfig: [{ field: "code" }, { field: "description" }],
            searchTerms,
          },
          advancedFilteringArgs: { advancedFilter: [ this.isNotFuelFilter ]},
        },
      })
    );
    this.isAllSelected = false;
  }

  checkProductSelected(product: ProductDTO) {
    const { code } = product;
    if (!this.selectedStoreProducts) {
      return true;
    }
    return !this.selectedStoreProducts.some(
      ({ productCode }) => productCode === code
    );
  }


  checkIsItemFavorite(product: ProductDTO) {
    const { code } = product;
    return this.selectedStoreProducts.some(({ productCode, favorite }) => productCode === code && favorite);
  } 

  favoriteClicked(product: ProductDTO) {
    const { code } = product;

    this.selectedStoreProducts = this.selectedStoreProducts.reduce((acc, cur) => {
      if (cur.productCode === code) {
        return [...acc, { ...cur, favorite: !cur.favorite }]
      }
      return [ ...acc, cur ];
    }, []);
    
    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  checkIsItemEnabled(product: ProductDTO) {
    const { code } = product;
    return this.selectedStoreProducts.some(({ productCode, enabled }) => productCode === code && enabled);
  }

  enableClicked(product: ProductDTO) {
    const { code } = product;

    this.selectedStoreProducts = this.selectedStoreProducts.reduce((acc, cur) => {
      if (cur.productCode === code) {
        return [...acc, { ...cur, enabled: !cur.enabled }]
      }
      return [ ...acc, cur ];
    }, []);

    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  priceChanged(product: ProductDTO) {
    const { code, unitPriceGross } = product;

    this.selectedStoreProducts = this.selectedStoreProducts.reduce((acc, cur) => {
      if (cur.productCode === code) {
        return [...acc, { ...cur, unitPriceGross }]
      }
      return [ ...acc, cur ];
    }, []);

    product.error = unitPriceGross < 0 || unitPriceGross > 999999.9999;
    
    if (this.storeProductsList.some((product) => product.error)) {
      this.snackbarService.show({
        message: this.translate.instant("Products.storeProductPriceInvalidExtended"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  selectAllChanged(event: MatCheckboxChange) {
    const { checked } = event;
    this.isAllSelected = checked;
    if (checked) {
      this.selectAll();
    } else {
      this.unselectAll();
    }
    this.cdr.detectChanges();
  }

  selectAll() {
    this.storeProductsList.forEach(({ code }) => {
      this.onSelectionChange(
        {
          options: [{ value: { code } }],
        },
        true
      );
    });
    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  unselectAll() {
    this.selectedStoreProducts = [];
    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  updateSiteStoreProducts(selectedStoreProducts: SiteProductDTO[]) {
    const { pageIndex, pageSize } = this.paginator;
    const siteStoreProductsList = selectedStoreProducts.map(
      ({ productCode, favorite, enabled, unitPriceGross }) => ({ 
        siteCode: this.siteCode, 
        productCode,
        favorite,
        enabled,
        unitPriceGross,
      })
    );

    this.store.dispatch(
      createStoreProductList({
        siteCode: this.siteCode,
        productList: siteStoreProductsList,
        searchTerms: this.searchTerms,
        paging: {
          realSize: true,
          skip: pageIndex * pageSize,
          top: pageSize,
        }
      })
    );

    this.isAllSelected = false;
  }


  ngOnDestroy() {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
  }
}
